<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

let hintPanelIdCounter = 0

const props = withDefaults(
  defineProps<{
    /** Accessible name for the ? trigger (override per use case). */
    triggerLabel?: string
    placement?: 'top' | 'bottom'
  }>(),
  {
    placement: 'bottom',
  }
)

const VIEWPORT_MARGIN = 12

const isOpen = ref(false)
const isPinned = ref(false)
const root = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const panelId = `sa-hint-panel-${++hintPanelIdCounter}`
const panelStyle = ref({
  top: '0px',
  left: '0px',
})

function hasHoverCapability(): boolean {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

/** Keeps the panel inside the viewport on narrow screens (centre + translateX(-50%)). */
function clampPanelCenterX(centerX: number, panelWidth: number): number {
  if (panelWidth <= 0) return centerX

  const half = panelWidth / 2
  const minCenter = VIEWPORT_MARGIN + half
  const maxCenter = window.innerWidth - VIEWPORT_MARGIN - half

  if (minCenter > maxCenter) {
    return window.innerWidth / 2
  }

  return Math.max(minCenter, Math.min(maxCenter, centerX))
}

function updatePanelPosition() {
  const trigger = root.value?.querySelector('.sa-hint__trigger') as HTMLElement | null
  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const gap = 8
  let centerX = rect.left + rect.width / 2
  const top =
    props.placement === 'top' ? rect.top - gap : rect.bottom + gap

  panelStyle.value = {
    top: `${top}px`,
    left: `${centerX}px`,
  }

  const panel = panelRef.value
  if (!panel) return

  const panelWidth = panel.getBoundingClientRect().width
  centerX = clampPanelCenterX(centerX, panelWidth)

  panelStyle.value = {
    top: `${top}px`,
    left: `${centerX}px`,
  }
}

function openPanel() {
  isOpen.value = true
}

function closePanel() {
  isOpen.value = false
  isPinned.value = false
}

function onTriggerClick(event: MouseEvent) {
  event.stopPropagation()
  if (isPinned.value) {
    closePanel()
    return
  }
  isPinned.value = true
  openPanel()
}

function onMouseEnter() {
  if (hasHoverCapability()) {
    openPanel()
  }
}

function onMouseLeave() {
  if (hasHoverCapability() && !isPinned.value) {
    isOpen.value = false
  }
}

function onDocumentClick(event: MouseEvent) {
  if (!isPinned.value) return
  if (!root.value?.contains(event.target as Node)) {
    const panel = document.getElementById(panelId)
    if (panel?.contains(event.target as Node)) return
    closePanel()
  }
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    closePanel()
  }
}

function onViewportChange() {
  if (isOpen.value) updatePanelPosition()
}

watch(isOpen, async (open) => {
  if (!open) return
  await nextTick()
  updatePanelPosition()
  await nextTick()
  updatePanelPosition()
  requestAnimationFrame(() => updatePanelPosition())
})

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onDocumentKeydown)
  window.addEventListener('resize', onViewportChange)
  window.addEventListener('scroll', onViewportChange, true)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onDocumentKeydown)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
})
</script>

<template>
  <span
    ref="root"
    class="sa-hint"
    :class="`sa-hint--${placement}`"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <button
      type="button"
      class="sa-hint__trigger"
      :aria-label="triggerLabel ?? $t('Dashboard.Hint.TriggerLabel')"
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      @click="onTriggerClick"
    >
      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          fill-rule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clip-rule="evenodd"
        />
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-show="isOpen"
        :id="panelId"
        ref="panelRef"
        class="sa-hint__panel"
        :class="`sa-hint__panel--${placement}`"
        :style="panelStyle"
        role="tooltip"
      >
        <p v-if="$slots.title" class="sa-hint__title">
          <slot name="title" />
        </p>
        <div class="sa-hint__body">
          <slot />
        </div>
      </div>
    </Teleport>
  </span>
</template>
